import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useHistory } from 'react-router-dom'

import clsx from 'clsx'
import makeStyles from '@material-ui/core/styles/makeStyles'
import PropTypes from 'prop-types'

import { useMediaQuery, useTheme } from '@material-ui/core'

import format from 'date-fns/format'

import { getDocument, Util } from 'pdfjs-dist/webpack'

import CircularProgress from '@material-ui/core/CircularProgress'
import Paper from '@material-ui/core/Paper'
import Tooltip from '@material-ui/core/Tooltip'

import {
  getScript,
  getScriptPageUrls,
  getPageResponses,
  getResponseStatuses,
  getScriptResponses,
  deleteScriptResponse,
  postPageResponse,
  postTermsAcceptance,
  putPageReaction,
  user,
  getPageReactions
} from './api'

import { useAppUser } from './app/hooks'
import debounce from './debounce'
import RATINGS from './models/ratings'
import { unexpectedErrorContainer } from './theme'
import { authorReview, needsSurvey, scriptIdFromProps, scriptIsDeleted, unfinished, unread } from './utility'

import ErrorResponse from './ErrorResponse'

import AuthorView from './reader-controllers/AuthorView'
import ConfirmAbandonDialog from './reader-controllers/ConfirmAbandonDialog'
import FinishedReview from './reader-controllers/FinishedReview'
import ReactionPopover from './reader-controllers/ReactionPopover'
import UnfinishedReview from './reader-controllers/UnfinishedReview'

const PAGE_LAYOUT_PADDING = [2, 4]

const useStyles = makeStyles(
  theme => ({
    root: {
      '& > section': {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        '& > *': {
          width: '100%'
        }
      }
    },

    authorView: {
      marginBottom: theme.spacing(1), // One less than top due to page margin.
      marginTop: theme.spacing(2)
    },

    pageLayout: {
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(2, 4)
      }
    },

    pageContainer: {
      margin: theme.spacing(1, 0, 2),
      padding: 0,
      position: 'relative',

      [theme.breakpoints.up('sm')]: {
        backgroundColor: theme.palette.divider,
        margin: theme.spacing(1, 0, 0.25),
        padding: theme.spacing(...PAGE_LAYOUT_PADDING)
      }
    },

    pageEmbed: {},

    pageSvg: {
      fontFamily: 'Courier',
      userSelect: 'none'
    },

    clickable: {
      cursor: 'pointer'
    },

    scriptText: {},
    marker: {},

    reaction: {
      fontFamily: theme.typography.body2.fontFamily,
      transition: theme.transitions.create('opacity'),

      '&:hover': {
        opacity: 0.5
      }
    },

    watermark: {
      alignItems: 'flex-end',
      bottom: 0,
      display: 'flex',
      fontSize: theme.typography.pxToRem(6),
      left: 0,
      position: 'absolute',
      right: 0,

      [theme.breakpoints.up('sm')]: {
        fontSize: theme.typography.pxToRem(12),
        padding: theme.spacing(...PAGE_LAYOUT_PADDING)
      }
    },

    watermarkFooter: {
      color: theme.palette.text.disabled,
      display: 'flex',
      flexGrow: 1,
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      padding: theme.spacing(2)
    },

    userTimestamp: {},
    user: {},
    timestamp: {},

    doNotDuplicate: {
      textTransform: 'uppercase'
    },

    reviewControls: {
      marginTop: theme.spacing(1)
    },

    unexpectedError: unexpectedErrorContainer(theme)
  }),
  {
    name: 'Reader'
  }
)

const DEFAULT_LIMIT = 20
const PAGE_KEY = 'cursor'
const LIMIT_KEY = 'limit'

const MINIMUM_READING_TIME = 0

const ALL_SENTIMENTS = [
  { id: 'funny', visual: '🤣', label: 'Funny' },
  { id: 'sad', visual: '😭', label: 'Sad' },
  { id: 'moving', visual: '🥲', label: 'Moving' },
  { id: 'scary', visual: '😱', label: 'Scary' },
  { id: 'confusing', visual: '❓', label: 'Confusing' },
  { id: 'like', visual: '👍', label: 'Like' },
  { id: 'dislike', visual: '👎', label: 'Dislike' }
]

const VIEW_BOX_DEFAULT = { width: 0, height: 0 }

// For convenience and readability: we name the indices in the transform matrix array that
// pertain to the role that index plays in the final transformation.
const X_SCALE = 0
const Y_SCALE = 3
const X_TRANSLATE = 4
const Y_TRANSLATE = 5

const getUserReviewed = listOfReviewedItems =>
  listOfReviewedItems.find(reviewedItem => reviewedItem.reviewer === user())

const getUserResponseStatus = async script => {
  // Work in progress.
  /*
     TODO This logic is an artifact of the original Riot-based implementation of the script reader. Loading the full
     list of response statuses can actually be deferred to the chosen reader controller for the current script and
     user. Instead, an API endpoint that only returns the user’s own response status for this script should be used
     here.
  */
  const params = new URLSearchParams()
  params.set(LIMIT_KEY, DEFAULT_LIMIT)

  let responseStatusesPage
  do {
    responseStatusesPage = await getResponseStatuses(script, params)

    const userResponseStatus = getUserReviewed(responseStatusesPage.statuses)
    if (userResponseStatus) {
      return userResponseStatus
    }

    if (responseStatusesPage.cursor_next) {
      params.set(PAGE_KEY, responseStatusesPage.cursor_next)
    }
  } while (responseStatusesPage.cursor_next)

  // If we get here, then the user has not started the script.
  return null
}

const getScriptData = async scriptId => {
  const script = await getScript(scriptId)

  // Software-level deleted script protection: API should do this, but for now we check on the front end.
  if (scriptIsDeleted(script) && script.author !== user()) {
    // Do a fake 404, mimicking what the API would do. The real error has a different message so this is the
    // inside tell (apart from the _actual_ lack of 404).
    const message = 'Not found: No such script'
    const simulated404 = new Error(message)
    simulated404.response = {
      status: 404,
      json: () => Promise.resolve({ message })
    }

    throw simulated404
  }

  const userResponseStatus = await getUserResponseStatus(script)
  return { script, userResponseStatus }
}

// Saving code is defined outside the function so that the debounced version can retain its state.
const save = async (script, pageNumber, rating, comment) => {
  const pageResponsePayload = {
    rating: RATINGS.keys.indexOf(rating),
    comment
  }

  await postPageResponse(script, pageNumber, pageResponsePayload)
}

const SAVE_DEBOUNCE_TIME = 250
const saveDebounced = debounce(save, SAVE_DEBOUNCE_TIME)

const MissingResponseStatus = props => {
  const { className } = props
  return (
    <section className={className}>
      <p>
        <strong>Missing Response Status</strong>
      </p>

      <p>Sorry—we were expecting a data thing called a “response status” for this script but didn’t get one.</p>

      <p>
        Please tell the Zoodǐker folks this specific message: “I’m supposed to have a response status for{' '}
        <em>(give them this script’s ID or title)</em>, but the API didn’t return one.”
      </p>

      <p>Unfortunately, without this response status, we don’t know how to proceed with this script.</p>
    </section>
  )
}

// Adapted from https://github.com/mozilla/pdf.js/blob/master/examples/text-only/pdf2svg.mjs
const SVG_NS = 'http://www.w3.org/2000/svg'

/**
 * Convenience function for taking a transform and setting it in an SVG matrix expression
 * (the transform is presumed to be well-formed).
 *
 * @param {array} transform 3x2 matrix representing the general svg transform
 *                          (i.e., array of six numbers in column-major order)
 *
 * @returns matrix expression string holding the transform
 */
const toMatrix = transform => `matrix(${transform.join(' ')})`

const hideMarker = marker => {
  // “Hidden” in this case means “area of zero” 😅
  marker.setAttribute('width', 0)
  marker.setAttribute('height', 0)
}

/**
 * This function takes two elements and sets the first element’s bounding box to be the
 * same as the second one’s.
 *
 * @param {object} source SVG element whose bounds we will copy
 * @param {object} destination SVG element to posiion over another
 */
const copyElementBounds = (source, destination) => {
  const { x, y, width, height } = source.getBBox()
  const transform = source.getAttribute('transform')

  destination.setAttribute('transform', transform)
  destination.setAttribute('x', x)
  destination.setAttribute('y', y)
  destination.setAttribute('width', width)
  destination.setAttribute('height', height)
}

const buildSvg = ({
  viewport,
  textContent,
  marker,
  classes,
  mouseEnterHandler,
  mouseClickHandler,
  showCommentHandler,
  hideCommentHandler,
  hoverCapable
}) => {
  // Building SVG with size of the viewport (for simplicity)
  const svg = document.createElementNS(SVG_NS, 'svg:svg')
  const { width, height, transform: viewportTransform } = viewport

  svg.append(marker)

  svg.setAttribute('class', classes.pageSvg)
  svg.setAttribute('width', '100%')
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`)
  // items are transformed to have 1px font size
  svg.setAttribute('font-size', 1)

  // Mouse movement within the “open” svg region will hide the marker.
  svg.onmousemove = event => {
    if (event.target === svg || event.target === marker) {
      hideMarker(marker)
    }
  }

  // Iterate through the text items, also creating a reaction placeholder for each.
  const textElementsAndTransforms = textContent.items.map((textItem, index) => {
    const { str, transform: textItemTransform } = textItem

    // we have to take in account viewport transform, which includes scale,
    // rotation and Y-axis flip, and not forgetting to flip text.
    const tx = Util.transform(Util.transform(viewportTransform, textItemTransform), [1, 0, 0, -1, 0, 0])

    // adding text element
    const textElement = document.createElementNS(SVG_NS, 'svg:text')
    textElement.setAttribute('transform', toMatrix(tx))
    textElement.onmouseenter = mouseEnterHandler(textElement, tx)
    textElement.textContent = str
    return { textElement, transform: tx }
  })

  // We place all text elements in the SVG first.
  textElementsAndTransforms.forEach(({ textElement }) => svg.append(textElement))

  // We only count non-empty strings for our “lines,” so we filter for them first.
  const nonEmptyElementsAndTransforms = textElementsAndTransforms.filter(({ textElement }) =>
    Boolean(textElement.textContent)
  )

  // Then we derive our actual lines by matching up the y-translation in their respective transforms.
  const lineMap = nonEmptyElementsAndTransforms.reduce((currentMap, textElementAndTransform) => {
    const { transform } = textElementAndTransform

    const yTranslation = transform[Y_TRANSLATE]
    const yKey = Math.floor(yTranslation)

    const existingLine = currentMap[yKey]
    if (existingLine) {
      existingLine.elements.push(textElementAndTransform)
    } else {
      currentMap[yKey] = { yKey, elements: [textElementAndTransform] }
    }

    return currentMap
  }, {})

  // The final lines array takes this map, sorts them by y-coordinate for consistency, then assigns
  // a reaction element to each item.
  const linesOfElements = Object.values(lineMap).sort((leftLine, rightLine) => {
    const { yKey: leftYKey } = leftLine
    const { yKey: rightYKey } = rightLine
    return leftYKey - rightYKey
  })

  const lines = linesOfElements.map(({ elements }, index) => {
    // Assign text element attributes that apply only to non-empty text elements.
    elements.forEach(({ textElement }) => {
      textElement.setAttribute('class', clsx(classes.clickable, classes.scriptText))
      textElement.onclick = mouseClickHandler(index)
      // ☝🏼 Visually, this means that clicks must take place on an actual text element and not the marker.
    })

    // Create the reaction element.
    const reactionElement = document.createElementNS(SVG_NS, 'svg:text')

    // ASSUMPTION: We need a transform to position the reaction element. For this, we take
    // the transform of the first element, assuming here that elements within the same y-key
    // will pretty much have the same one.
    const transform = elements[0].transform // The way lineMap is built guarantees a non-empty array.

    // We use a fixed class name here bcause we are outside the realm of JSS.
    // But the useStyles function should take this into account.
    reactionElement.setAttribute('class', clsx(classes.clickable, classes.reaction))
    reactionElement.setAttribute('transform', toMatrix(transform))
    reactionElement.textContent = ''
    reactionElement.onclick = mouseClickHandler(index)

    // No tooltips for touch screens—they just show the reaction popover via click.
    if (hoverCapable) {
      reactionElement.onmouseenter = showCommentHandler(index)
      reactionElement.onmouseleave = hideCommentHandler(index)
    }

    return { elements, transform, reactionElement }
  })

  // Now we add the reaction elements.
  lines.forEach(({ reactionElement }) => svg.append(reactionElement))

  return { svg, width, height, lines }
}

const buildMarker = (theme, classes) => {
  const rectangle = document.createElementNS(SVG_NS, 'svg:rect')

  rectangle.setAttribute('class', classes.marker)
  rectangle.setAttribute('rx', 0.25)
  rectangle.setAttribute('fill', theme.palette.primary.main)
  rectangle.setAttribute('fill-opacity', 0.25)

  hideMarker(rectangle)
  return rectangle
}

const Reader = props => {
  const appUser = useAppUser()
  const now = new Date()

  const watermarkUser = appUser?.username
  const watermarkTimestamp = format(now, 'yyyy-MM-dd h:mmaaa')

  // Top-level script and response state.
  const [script, setScript] = useState(null)
  const [userResponseStatus, setUserResponseStatus] = useState(null)
  const [replacedResponseStatus, setReplacedResponseStatus] = useState(null)
  const [userPageResponse, setUserPageResponse] = useState(null)

  const { status: readerStatus } = userResponseStatus ?? {}

  // Page reading/tracking state.
  const [pageInProgress, setPageInProgress] = useState(0)
  const [pageNumber, setPageNumber] = useState(0)
  const [pageUrl, setPageUrl] = useState()

  // Reactions-related state.
  const [reactions, setReactions] = useState([])
  const [reactionAnchor, setReactionAnchor] = useState()
  const [selectedReaction, setSelectedReaction] = useState()
  const [selectedReactionIndex, setSelectedReactionIndex] = useState(-1)
  const [sentiments, setSentiments] = useState([])
  const [sentimentsMap, setSentimentsMap] = useState({})

  // Comment hover-related state and values.
  const hoverCapable = useMediaQuery('(hover: hover)') // h/t https://css-irl.info/detecting-hover-capable-devices/
  const [commentAnchor, setCommentAnchor] = useState()
  const [commentTooltip, setCommentTooltip] = useState('')
  const [selectedCommentIndex, setSelectedCommentIndex] = useState(-1)

  // Classes need to be available earlier on because we need them when constructing the script page SVG.
  const classes = useStyles(props)

  // PDF-content-related state and values.
  const theme = useTheme()
  const marker = useMemo(() => buildMarker(theme, classes), [theme, classes])

  const [lines, setLines] = useState()

  // Page-rendering-related state and values.
  const [pageRender, setPageRender] = useState()
  const [pageEmbed, setPageEmbed] = useState()
  const [viewBox, setViewBox] = useState(VIEW_BOX_DEFAULT)

  const { width: viewBoxWidth } = viewBox

  // Timeout-related state.
  const [readingTimeout, setReadingTimeout] = useState(null)
  const [minimumReadingTimeLapsed, setMinimumReadingTimeLapsed] = useState(false)

  const [comment, setComment] = useState('')
  const [rating, setRating] = useState(null)
  const [loading, setLoading] = useState(false)

  const [consideringAbandon, setConsideringAbandon] = useState(false)

  const [error, setError] = useState(null)

  const pageEmbedReady = useCallback(node => setPageEmbed(node), [])

  const updateMarkerPosition = useCallback(
    (text, transform) => {
      // First, copy the original text bounds.
      copyElementBounds(text, marker)

      // The marker spans the full width of a page, so we only transform along the y-axis.
      const markerMatrix = [1, 0, 0, transform[Y_SCALE], 0, transform[Y_TRANSLATE]]
      marker.setAttribute('transform', toMatrix(markerMatrix))
      marker.setAttribute('width', viewBoxWidth)
    },
    [marker, viewBoxWidth]
  )

  const history = useHistory()

  const scriptId = scriptIdFromProps(props)
  const onLastPage = () => pageNumber >= script.page_count

  const timerPage = replacedResponseStatus
    ? Math.max(pageInProgress, replacedResponseStatus.page_response_count + 1)
    : pageInProgress

  const navigateToSurvey = useCallback(() => history.push(`/surveys/${scriptId}`), [history, scriptId])

  const unabandon = async () => {
    setLoading(true)
    const scriptResponses = await getScriptResponses(script)
    const userScriptResponse = scriptResponses.find(scriptResponse => scriptResponse.reviewer === user())

    if (!userScriptResponse) {
      // This really shouldn't be possible, but still...
      throw new Error(`Script response not found for ${user()}`)
    }

    await deleteScriptResponse(userScriptResponse)
    history.push('/') // Return to the dashboard to show change of script state.
  }

  const handleCancelAbandon = event => setConsideringAbandon(false)

  const handleConfirmAbandon = async event => {
    await postPageResponse(script, pageNumber, { rating: RATINGS.keys.indexOf(rating), abandon: true })
    navigateToSurvey()
  }

  const mouseEnterHandler = useCallback(
    (text, transform) => event => {
      if (unfinished(readerStatus)) {
        updateMarkerPosition(text, transform)
      }
    },
    [readerStatus, updateMarkerPosition]
  )

  const mouseClickHandler = useCallback(
    lineIndex => event => {
      if (unfinished(readerStatus)) {
        // We have to set the line index first because referencing reactions here will
        // cause an event loop. Instead, a useEffect will pick up the new index and take
        // things from there.
        setSelectedReactionIndex(lineIndex)
        setReactionAnchor(event.target)
      }
    },
    [readerStatus]
  )

  const showCommentHandler = useCallback(lineIndex => event => setSelectedCommentIndex(lineIndex), [])
  const hideCommentHandler = useCallback(lineIndex => event => setSelectedCommentIndex(-1), [])

  const handleCommentClose = event => setCommentAnchor(null)
  const handleReactionClose = event => setReactionAnchor(null)

  const handleReactionSubmit = submission => {
    const { line } = selectedReaction
    const reactionSubmission = { line, ...submission }
    const { sentiment, comment } = submission

    // API interaction is fire-and-forget.
    const payload = { type: sentiment, comment }
    putPageReaction(script, pageNumber, line, payload)

    // See if there is an existing reaction.
    const currentReactionIndex = reactions.findIndex(reaction => reaction.line === line)
    if (currentReactionIndex === -1) {
      setReactions([...reactions, reactionSubmission])
    } else {
      if (sentiment || comment) {
        setReactions([
          ...reactions.slice(0, currentReactionIndex),
          reactionSubmission,
          ...reactions.slice(currentReactionIndex + 1)
        ])
      } else {
        // A submission with neither a sentiment nor a comment is considered to be a deletion.
        setReactions(reactions.filter(reaction => reaction.line !== line))
      }
    }

    handleReactionClose()
  }

  useEffect(() => {
    // TODO Substitute with an API call when it is ready.
    let active = true

    const retrieveSentiments = async () => {
      if (!active) {
        return
      }

      setSentiments(ALL_SENTIMENTS)
    }

    retrieveSentiments()
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    setSentimentsMap(Object.fromEntries(sentiments.map(item => [item.id, item])))
  }, [sentiments])

  useEffect(() => {
    if (!userResponseStatus) {
      return
    }

    setMinimumReadingTimeLapsed(pageInProgress !== timerPage || !unfinished(userResponseStatus.status))
  }, [pageInProgress, timerPage, userResponseStatus])

  useEffect(() => {
    if (minimumReadingTimeLapsed && readingTimeout !== null) {
      clearTimeout(readingTimeout)
      setReadingTimeout(null)
    } else if (!minimumReadingTimeLapsed && readingTimeout === null) {
      setReadingTimeout(
        setTimeout(() => {
          setMinimumReadingTimeLapsed(true)
        }, MINIMUM_READING_TIME)
      )
    }
  }, [minimumReadingTimeLapsed, readingTimeout])

  useEffect(() => {
    if (!script || pageNumber < 1) {
      return
    }

    const loadPage = async () => {
      const [pageUrls, pageResponsesPage, reactions] = await Promise.all([
        getScriptPageUrls(script, pageNumber),
        getPageResponses(script, pageNumber),
        getPageReactions(script, pageNumber)
      ])

      const pageResponses = pageResponsesPage.responses
      const userPageResponse = getUserReviewed(pageResponses)
      if (userPageResponse) {
        setUserPageResponse(userPageResponse)
      } else {
        setComment('')
        setRating(null)
      }

      setReactions(reactions ? reactions.map(({ line, comment, type }) => ({ line, comment, sentiment: type })) : [])

      // Adapted from https://github.com/mozilla/pdf.js/blob/master/examples/text-only/pdf2svg.mjs
      const { promise } = getDocument(pageUrls)
      const pdfDocument = await promise
      const page = await pdfDocument.getPage(1)
      const viewport = page.getViewport({ scale: 1.0 })
      const textContent = await page.getTextContent()

      // Reset the marker.
      hideMarker(marker)

      const { svg, width, height, lines } = buildSvg({
        viewport,
        textContent,
        marker,
        classes,
        mouseEnterHandler,
        mouseClickHandler,
        showCommentHandler,
        hideCommentHandler,
        hoverCapable
      })

      setLines(lines)
      setPageRender(svg)
      setViewBox({ width, height })
      page.cleanup()

      setPageUrl(pageUrls.url)
      setLoading(false)
    }

    setLoading(true)
    loadPage()
  }, [
    pageNumber,
    script,
    marker,
    classes,
    mouseEnterHandler,
    mouseClickHandler,
    showCommentHandler,
    hideCommentHandler,
    hoverCapable
  ])

  useEffect(() => {
    if (!pageUrl) {
      return
    }

    document.body.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'start'
    })
  }, [pageUrl])

  useEffect(() => {
    setPageNumber(pageInProgress)
  }, [pageInProgress])

  useEffect(() => {
    const startReadingSession = async ({ script, userResponseStatus }) => {
      if (authorReview(script) || !userResponseStatus) {
        setPageNumber(1)
        return
      }

      const { status: readerStatus, page_response_count: pageResponseCount } = userResponseStatus
      if (unfinished(readerStatus)) {
        // If there are zero page responses so far, we go to page 1.
        if (pageResponseCount === 0) {
          setPageInProgress(1)
        } else {
          // Otherwise check the page response on the "count"-th page. If it has a rating already, we need to go to
          // the page _after_ that.
          let targetPage = pageResponseCount
          const pageResponsesPage = await getPageResponses(script, targetPage)
          const userPageResponse = getUserReviewed(pageResponsesPage.responses)

          if (userPageResponse && userPageResponse.rating > -1 && targetPage < script.page_count) {
            targetPage += 1
          }

          if (userPageResponse) {
            setUserPageResponse(userPageResponse)
          } else {
            setComment('')
            setRating(null)
          }

          setPageInProgress(targetPage)
        }
      } else if (needsSurvey(readerStatus)) {
        navigateToSurvey()
      } else {
        setPageNumber(1)
      }
    }

    const loadScript = async () => {
      try {
        const { script, userResponseStatus } = await getScriptData(scriptId)
        setScript(script)
        setUserResponseStatus(userResponseStatus)

        // One more step is necessary with scripts that are the replacement of another.
        //
        // This step is optional; if loading that script fails, we proceed as if the current
        // script is the original version.
        if (script.replacement_of) {
          try {
            const { userResponseStatus: replacedResponseStatus } = await getScriptData(script.replacement_of)
            setReplacedResponseStatus(replacedResponseStatus)
          } catch (replacementError) {
            // No-op: We proceed as if the current script is the original version.
          }
        }

        startReadingSession({ script, userResponseStatus })
      } catch (error) {
        setError(error)
      }
    }

    loadScript()
  }, [scriptId, navigateToSurvey])

  useEffect(() => {
    setComment(userPageResponse ? userPageResponse.comment : '')
    setRating(userPageResponse ? RATINGS.keys[userPageResponse.rating] : null)
  }, [userPageResponse])

  useEffect(() => {
    if (pageRender && pageEmbed) {
      pageEmbed.innerHTML = ''
      pageEmbed.append(pageRender)

      // Nothing further to do if we don’t have lines or reactions yet.
      if (!lines || !reactions) {
        return
      }

      // Bounding boxes aren’t available until the svg element is appended to the page,
      // so that’s why this step is performed here rather than in the processing stage.
      lines.forEach((line, index) => {
        const { reactionElement, transform } = line

        // Reactions appear at the end of each line, so we change the x-translate part
        // of the transform based on the view box’s width.
        const reactionTransform = [...transform]
        reactionTransform[X_TRANSLATE] = viewBoxWidth - transform[X_SCALE]

        reactionElement.setAttribute('text-anchor', 'end')
        reactionElement.setAttribute('transform', toMatrix(reactionTransform))
        reactionElement.textContent = ''

        // Now for the content: populate the reaction elements that have a corresponding reaction.
        const reaction = reactions.find(reaction => reaction.line === index)
        if (reaction) {
          const { sentiment, comment } = reaction
          const { visual } = sentimentsMap[sentiment] ?? {}
          reactionElement.textContent = `${visual ? `${visual} ` : ''}${comment ? '✏️' : ''}`
        }
      })
    }
  }, [pageRender, pageEmbed, lines, reactions, sentimentsMap, viewBoxWidth])

  useEffect(() => {
    if (selectedReactionIndex === -1) {
      return
    }

    // Determine if the latest reaction index corresponds to a current reaction,
    // then act accordingly.
    const selectedReaction = reactions.find(reaction => reaction.line === selectedReactionIndex)
    setSelectedReaction(selectedReaction ?? { line: selectedReactionIndex })
  }, [reactions, selectedReactionIndex])

  useEffect(() => {
    if (selectedCommentIndex === -1) {
      setCommentAnchor(null)
      return
    }

    // Determine if the latest comment index corresponds to a current reaction with a comment,
    // then act accordingly.
    const selectedReaction = reactions.find(reaction => reaction.line === selectedCommentIndex)
    const { line, comment } = selectedReaction ?? {}
    if (!comment) {
      return
    }

    // If we have a comment, then we go ahead and show it.
    setCommentTooltip(comment)
    setCommentAnchor(lines[line]?.reactionElement)
  }, [lines, reactions, selectedCommentIndex])

  const readerProps = {
    script,
    responseStatus: userResponseStatus,
    pageNumber,
    setPageNumber,
    pageInProgress,
    minimumReadingTimeLapsed,
    replacedResponseStatus,
    loading,

    showTerms: Boolean(script && unread(readerStatus) && !authorReview(script)),
    agreeToTerms: async () => {
      try {
        await postTermsAcceptance(script)

        // Upon terms acceptance, reload the script response status.
        const userResponseStatus = await getUserResponseStatus(script)
        setUserResponseStatus(userResponseStatus)
      } catch (error) {
        setError(error)
      }
    },

    onLastPage,
    timerPage,

    abandon: event => setConsideringAbandon(true),

    availablePages: () => {
      const result = []

      // It's important to evaluate authorReview first because we need to short-circuit past unfinished if false.
      const lastAvailablePage = !authorReview(script) && unfinished(readerStatus) ? pageInProgress : script.page_count

      for (let i = 1; i <= lastAvailablePage; i += 1) {
        result.push(i)
      }

      return result
    },

    ratePage: async rating => {
      setLoading(true)

      try {
        await save(script, pageNumber, rating, comment)
      } catch (error) {
        setError(error)
      }

      if (onLastPage()) {
        setRating(rating)
        navigateToSurvey()
      } else {
        setPageInProgress(pageNumber + 1)
      }
    },

    comment,
    updateComment: comment => {
      setComment(comment)
      saveDebounced(script, pageNumber, rating, comment)
    },

    rating,
    updateRating: rating => {
      setRating(rating)

      try {
        save(script, pageNumber, rating, comment)
      } catch (error) {
        setError(error)
      }
    },

    unabandon
  }

  return (
    <div className={classes.root}>
      <section>
        {error ? (
          <div className={classes.unexpectedError}>
            <div>Sorry, but we appear to have run into a problem with this script.</div>
            <ErrorResponse error={error} />
          </div>
        ) : script && pageUrl ? (
          <>
            {authorReview(script) && <AuthorView className={classes.authorView} {...readerProps} />}

            <section className={classes.pageContainer}>
              <Paper className={classes.pageEmbed} ref={pageEmbedReady} />

              <section className={classes.watermark}>
                <footer className={classes.watermarkFooter}>
                  <span className={classes.userTimestamp}>
                    <span className={classes.user}>{watermarkUser ?? ''}</span>{' '}
                    <span className={classes.timestamp}>({watermarkTimestamp})</span>
                  </span>

                  <span className={classes.doNotDuplicate}>Do not duplicate</span>
                </footer>
              </section>
            </section>

            <div className={classes.reviewControls}>
              {!authorReview(script) &&
                (userResponseStatus ? (
                  unfinished(readerStatus) ? (
                    <UnfinishedReview {...readerProps} />
                  ) : (
                    <FinishedReview {...readerProps} />
                  )
                ) : (
                  <MissingResponseStatus className={classes.unexpectedError} />
                ))}
            </div>

            <ConfirmAbandonDialog
              open={consideringAbandon}
              onClose={handleCancelAbandon}
              onConfirm={handleConfirmAbandon}
              pageNumber={pageNumber}
            />
          </>
        ) : (
          <CircularProgress />
        )}
      </section>

      <ReactionPopover
        open={Boolean(reactionAnchor)}
        anchorEl={reactionAnchor}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        onClose={handleReactionClose}
        onSubmit={handleReactionSubmit}
        sentiments={sentiments}
        initialReaction={selectedReaction}
      />

      <Tooltip
        arrow
        PopperProps={{ anchorEl: commentAnchor }}
        open={Boolean(commentAnchor)}
        title={commentTooltip}
        onClose={handleCommentClose}
      >
        {/* Just a placeholder: we are setting anchorEl directly. */}
        <span></span>
      </Tooltip>
    </div>
  )
}

Reader.propTypes = {
  className: PropTypes.string
}

export default Reader
